import React, { useEffect } from "react";
import { Image, View, TouchableOpacity, StyleSheet } from "react-native";
import {
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import {
  CardTitleText,
  DocumentRow,
  MoonDateField,
  MoonFormField,
} from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import Text from "@src/packages/medical-history/components/MedicalHistoryText";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { Package, FileText, Plus } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type { MedicationsVaccinesStackParamList } from "../MedicationsVaccinesNavigator";
import {
  VaccineDetail,
  VaccineType,
  useMedicationsVaccines,
} from "../MedicationsVaccinesContext";
import { getVaccineLabelKey } from "../options";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";
import { useDateFormat } from "@src/packages/utils/useDateFormat";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const vaccineIllustration =
  require("@src/apps/moon/assets/images/injection-pen.png") as number;

export function VaccineDetailsScreen() {
  const t = useTranslations();
  const capturePress = usePressCapture("VaccineDetailsScreen");
  const { state, updateState } = useMedicationsVaccines();
  const theme = useMedicalHistoryTheme();
  const { dateFormat } = useDateFormat();
  const navigation =
    useNavigation<
      NativeStackNavigationProp<MedicationsVaccinesStackParamList>
    >();

  useEffect(() => {
    const selected = state.selectedVaccines.filter((v) => v !== "none");
    if (selected.length === 0) {
      navigation.goBack();
      return;
    }

    const selectedSet = new Set(selected);
    let nextDetails = [...state.vaccineDetails];

    if (nextDetails.length === 0) {
      nextDetails = selected.map((v) => ({
        vaccine: v,
        date: null,
        brand: null,
        notes: null,
      }));
      updateState({ vaccineDetails: nextDetails });
      return;
    }

    let changed = false;

    selected.forEach((v) => {
      if (!nextDetails.some((detail) => detail.vaccine === v)) {
        nextDetails.push({ vaccine: v, date: null, brand: null, notes: null });
        changed = true;
      }
    });

    const filtered = nextDetails.filter((detail) => {
      if (!detail.vaccine) return true;
      if (selectedSet.has(detail.vaccine)) return true;
      return detail.vaccine === "other";
    });

    if (filtered.length !== nextDetails.length) {
      nextDetails = filtered;
      changed = true;
    }

    if (changed) {
      updateState({ vaccineDetails: nextDetails });
    }
  }, [state.selectedVaccines, state.vaccineDetails, updateState, navigation]);

  // Pre-populate "other" vaccine notes with the specify text from selection screen.
  // Intentionally only re-runs when the length changes (i.e. a new "other" row is appended).
  const vaccineDetailsLength = state.vaccineDetails.length;
  useEffect(() => {
    if (!state.otherVaccineText) {
      return;
    }
    const otherIdx = state.vaccineDetails.findIndex(
      (d) => d.vaccine === "other" && !d.notes,
    );
    if (otherIdx === -1) {
      return;
    }
    const next = state.vaccineDetails.map((detail, i) =>
      i === otherIdx ? { ...detail, notes: state.otherVaccineText } : detail,
    );
    updateState({ vaccineDetails: next, otherVaccineText: "" });
  }, [
    vaccineDetailsLength,
    state.vaccineDetails,
    state.otherVaccineText,
    updateState,
  ]);

  const handleAddAnother = () => {
    updateState({
      vaccineDetails: [
        ...state.vaccineDetails,
        { vaccine: null, date: null, brand: null, notes: null },
      ],
    });
  };

  const handleChange = (index: number, updates: Partial<VaccineDetail>) => {
    const next = state.vaccineDetails.map((detail, i) =>
      i === index ? { ...detail, ...updates } : detail,
    );
    updateState({ vaccineDetails: next });
  };

  const handleRemove = (index: number) => {
    const removedVaccine = state.vaccineDetails[index];
    const next = state.vaccineDetails.filter((_, i) => i !== index);

    // Track the deleted vaccine ID if it exists (from server)
    const vaccineIdsToDelete = removedVaccine?.id
      ? [...(state.vaccineIdsToDelete || []), removedVaccine.id]
      : state.vaccineIdsToDelete || [];

    updateState({ vaccineDetails: next, vaccineIdsToDelete });
  };

  const handleSave = () => {
    // Navigate to complete screen instead of saving inline
    navigation.navigate("MedicationsVaccinesComplete");
  };

  const selectedSet = new Set(
    state.selectedVaccines.filter((v) => v !== "none"),
  );

  const fallbackVaccineLabel = t(
    "med_history.medical.medications_vaccines.vaccine_card_fallback",
  );

  return (
    <QuestionLayout
      title={t(
        "med_history.medical.medications_vaccines.vaccine_details_title",
      )}
      subtitle={t(
        "med_history.medical.medications_vaccines.vaccine_details_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveLabel={t("med_history.medical.shared.continue")}
    >
      {state.vaccineDetails.map((detail, index) => {
        const label = detail.vaccine
          ? t(getVaccineLabelKey(detail.vaccine as VaccineType))
          : fallbackVaccineLabel;
        const isManualAddition =
          !detail.vaccine || !selectedSet.has(detail.vaccine);

        return (
          <View key={index} style={styles.cardWrapper}>
            <View style={[GLASS_CARD_STYLE, styles.detailCard]}>
              <View style={styles.cardHeader}>
                <CardTitleText>
                  {t(
                    "med_history.medical.medications_vaccines.vaccine_card_title",
                    { params: { label } },
                  )}
                </CardTitleText>
                {isManualAddition ? (
                  <TouchableOpacity
                    onPress={() => {
                      capturePress(
                        pressLabel(t("med_history.medical.shared.delete")),
                        "med_history.medical.shared.delete",
                      );
                      handleRemove(index);
                    }}
                  >
                    <Text className="text-rose-600" style={styles.deleteText}>
                      {t("med_history.medical.shared.delete")}
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
              <MoonDateField
                label={t(
                  "med_history.medical.medications_vaccines.date_received_label",
                )}
                placeholder={dateFormat}
                value={
                  detail.date
                    ? (detail.date.toISOString().split("T")[0] ?? "")
                    : ""
                }
                onChange={(iso) =>
                  handleChange(index, { date: iso ? new Date(iso) : null })
                }
              />

              <MoonFormField
                label={t(
                  "med_history.medical.medications_vaccines.brand_label",
                )}
                labelIcon={Package}
              >
                <MedicalHistoryTextField
                  placeholder={t(
                    "med_history.medical.medications_vaccines.brand_placeholder",
                  )}
                  value={detail.brand ?? ""}
                  onChangeText={(text) => handleChange(index, { brand: text })}
                  appearance={theme.inputAppearance}
                  style={{ width: "100%" }}
                />
              </MoonFormField>

              <MoonFormField
                label={t(
                  "med_history.medical.medications_vaccines.notes_label",
                )}
                labelIcon={FileText}
              >
                <MedicalHistoryTextField
                  placeholder={t(
                    "med_history.medical.medications_vaccines.notes_placeholder",
                  )}
                  value={detail.notes ?? ""}
                  onChangeText={(text) => handleChange(index, { notes: text })}
                  multiline
                  numberOfLines={3}
                  appearance={theme.inputAppearance}
                  style={{ width: "100%" }}
                />
              </MoonFormField>
            </View>
          </View>
        );
      })}
      <DocumentRow
        onPress={handleAddAnother}
        accessibilityLabel={t(
          "med_history.medical.medications_vaccines.add_another_vaccine",
        )}
        leading={
          <Image
            source={vaccineIllustration}
            resizeMode="contain"
            style={styles.addIllustration}
          />
        }
        title={t(
          "med_history.medical.medications_vaccines.add_another_vaccine",
        )}
        trailing={
          <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
        }
        showChevron={false}
        style={styles.addRow}
      />
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  cardWrapper: {
    marginBottom: 8,
  },
  detailCard: {
    gap: 16,
    padding: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  deleteText: {
    fontSize: 14,
    fontWeight: "600",
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});

export default VaccineDetailsScreen;
